import * as pc from "playcanvas";

const glsl = (x: TemplateStringsArray): string => x.join("");

const vertShader = glsl`
attribute vec2 vertex_position;
varying highp vec2 uv0;
void main()
{
  gl_Position = vec4(vertex_position, 0.5, 1.0);
  uv0 = vertex_position.xy * 0.5 + 0.5;
}
`;

const fragShader = glsl`
varying highp vec2 uv0;
uniform highp sampler2D uSceneDepthMap;
uniform vec4 uClipRect;
  // x: 1 / camera_far
  // y: camera_far
  // z: camera_near
  // w: is_ortho
uniform vec4 camera_params;


float linearizeDepth(float z) {
  if (camera_params.w == 0.0)
      return (camera_params.z * camera_params.y) / (camera_params.y + z * (camera_params.z - camera_params.y));
  else
      return camera_params.z + z * (camera_params.y - camera_params.z);
}

vec4 packFloatToRGBA8(float depth) {
    // Multiply the depth by 255 for the first 8 bits.
    float r = depth * 255.0;
    // “integer part” goes into R. 
    float rFloor = floor(r);
    // “fractional part” remains for the next channel.
    float rFrac  = fract(r);

    float g = rFrac * 255.0;
    float gFloor = floor(g);
    float gFrac  = fract(g);

    float b = gFrac * 255.0;
    float bFloor = floor(b);
    float bFrac  = fract(b);

    float a = bFrac * 255.0;
    float aFloor = floor(a);

    // Convert back to [0,1].
    return vec4(rFloor, gFloor, bFloor, aFloor) / 255.0;
}

void main(void) {
  highp vec2 sampleUV = uClipRect.xy + (uv0 * uClipRect.zw);
  highp float depth = linearizeDepth(texture2D(uSceneDepthMap, sampleUV).r);
  pcFragColor0 = depth;
}
`;

export type DepthBufferReadbackOptions = {
  radiusPX: number;
};

export class DepthBufferReadback {
  private _shader: pc.Shader;
  private _radiusPX: number;
  private _readbackSize: number;
  private _device: pc.GraphicsDevice;

  private _clipRectUniform = new Float32Array(4);
  private _cameraParamsUniform = new Float32Array(4);

  private _packedDepthResult: Uint8Array;

  private _renderTarget: pc.RenderTarget;

  public get radiusPX(): number {
    return this._radiusPX;
  }
  public set radiusPX(value: number) {
    this._radiusPX = value;
    this._allocateRenderTarget();
    this._readbackSize = Math.floor(this._radiusPX * 2);
    this._packedDepthResult = new Uint8Array(this._readbackSize * this._readbackSize * 4);
  }

  constructor(options: DepthBufferReadbackOptions = { radiusPX: 4 }) {
    this._device = window.app.graphicsDevice;
    this._shader = pc.createShaderFromCode(
      this._device,
      vertShader,
      fragShader,
      "DepthBufferReadbackShader",
      undefined,
      false,
      {
        fragmentOutputTypes: "float",
      }
    );
    this._radiusPX = options.radiusPX;
    this._renderTarget = this._allocateRenderTarget();
    this._readbackSize = Math.floor(this._radiusPX * 2) + 1;
    this._packedDepthResult = new Uint8Array(this._readbackSize * this._readbackSize * 4);
  }

  public async getDepthRegionAsync(x: number, y: number): Promise<undefined | Float32Array> {
    this._setupReadback(x, y);
    const size = this._readbackSize;
    const renderTarget = this._renderTarget;

    const result = await renderTarget.colorBuffer.read(0, 0, size, size, {
      data: new Float32Array(this._readbackSize * this._readbackSize),
      renderTarget,
    });
    if (result instanceof Float32Array) return result;
    // this._depthResult = new Float32Array(this._packedDepthResult.buffer);
    // for (let i = 0; i < this._depthResult.length; i++) {
    //   this._depthResult[i] = this._depthResult[i] * (f - n) + n;
    // }
    return undefined;
  }

  private _setupReadback(x: number, y: number): void {
    const cameraComponent = window.activeCamera?.camera;
    if (!cameraComponent) {
      return;
    }

    if (!this._renderTarget) {
      throw new Error("Render target not allocated");
    }

    const device = this._device;
    const renderTarget = this._renderTarget;

    const size = this._readbackSize;
    const normalizedWidth = size / window.sceneDepthTexture.width;
    const normalizedHeight = size / window.sceneDepthTexture.height;

    // Note: X and Y are in normalized coordinates already
    this._clipRectUniform[0] = x - normalizedWidth / 2;
    this._clipRectUniform[1] = y - normalizedHeight / 2;
    this._clipRectUniform[2] = normalizedWidth;
    this._clipRectUniform[3] = normalizedHeight;

    const f = cameraComponent.farClip;
    const n = cameraComponent.nearClip;
    this._cameraParamsUniform[0] = 1 / f;
    this._cameraParamsUniform[1] = f;
    this._cameraParamsUniform[2] = n;
    this._cameraParamsUniform[3] = cameraComponent.projection === pc.PROJECTION_ORTHOGRAPHIC ? 1 : 0;
    device.scope.resolve("camera_params").setValue(this._cameraParamsUniform);

    const shader = this._shader;
    device.scope.resolve("uSceneDepthMap").setValue(window.sceneDepthTexture);
    device.scope.resolve("uClipRect").setValue(this._clipRectUniform);
    pc.drawQuadWithShader(device, renderTarget, shader);
  }

  private _allocateRenderTarget() {
    if (this._renderTarget) {
      this._renderTarget.destroy();
    }

    const size = this._radiusPX * 2;

    const destTexture = new pc.Texture(this._device, {
      width: size,
      height: size,
      format: pc.PIXELFORMAT_R32F,
      mipmaps: false,
      minFilter: pc.FILTER_NEAREST,
      magFilter: pc.FILTER_NEAREST,
      addressU: pc.ADDRESS_CLAMP_TO_EDGE,
      addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    });

    this._renderTarget = new pc.RenderTarget({
      colorBuffer: destTexture,
      depth: false,
    });
    return this._renderTarget;
  }
}
